import { ExpandMore, InfoOutlined, KeyboardArrowRight } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box, Checkbox, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { t } from 'i18next';
import { ChangeEvent, memo, useEffect, useRef, useState } from 'react';
import { CustomNoRowsOverlay } from '../../../common/components/CustomNoRowsOverlay';
import { FilterStatusBadge } from '../../../common/components/FilterStatusBadge';
import { getFilterStatusLabel } from '../../../common/enums/FilterStatus';
import { SEVERITY_COLORS, Severity, getLabel } from '../../../common/enums/Severity';
import { ItemCard, MultiSelectItemCard } from '../../../common/instances/components/ItemCard';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { FeatureFlagsType, getFeatureFlag } from '../../../config/launch-darkly/FeatureFlagsController';
import { CheckedStatus, ReportExportState } from '../../../exports/triggers/states/ReportExportState';
import { PATHS } from '../../../routing/routes';
import { SeverityFilter } from '../common/components/SeverityFilter';
import { Issue } from '../models/Issue';

type Props = {
  title: string;
  tooltip: string;
  issues: Issue[];
  severityFilters: SeverityFilter[];
  index: number;
  selectedIssueNumber: number | null;
  onIssueFiltered: () => void;
};

export const IssueGroup = memo(({ title, tooltip, issues, severityFilters, index, selectedIssueNumber, onIssueFiltered }: Props) => {
  const isJiraEnhancementsEnabled = getFeatureFlag(FeatureFlagsType.ENABLE_JIRA_BATCH_EXPORT);
  const canMultiSelect = severityFilters.length > 0 && isJiraEnhancementsEnabled;
  const availableSeverities = Array.from(new Set(issues.map((i) => i.getSeverity())));
  const selectedIssue = issues.find((i) => i.getNumber() === selectedIssueNumber);

  const selectedIssueRef = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(index === 0 || !!selectedIssue);
  const [selectedSeverities, setSelectedSeverities] = useState(availableSeverities);
  const [exportState] = useState(ReportExportState.getInstance());

  useEffect(() => {
    setTimeout(() => {
      if (!selectedIssueRef.current) return;
      selectedIssueRef.current.scrollIntoView({ block: 'center' });
    }, 300);
  }, []);

  const onFilterChange = (severities: Severity[]) => {
    if (selectedIssue && !severities.includes(selectedIssue.getSeverity())) {
      onIssueFiltered();
    }
    setSelectedSeverities(severities);
  };

  const severityLabels = availableSeverities.map((s) => getLabel(s));
  const displayedIssues: Issue[] = issues.filter((i) => selectedSeverities.includes(i.getSeverity()));

  const availableInstances = displayedIssues.map((issue) => issue.getInstances()).flat();
  const selectedStatus = exportState.getIssueGroupSelectionState(availableInstances);

  const handleIssueClick = (e: ChangeEvent<HTMLInputElement>, issue: Issue) => {
    const instanceIds = issue.getInstances();
    if (e.target.checked) {
      exportState.selectAllInstances(instanceIds, issue.getNumber());
    } else {
      exportState.unselectAllInstances(instanceIds, issue.getNumber());
    }
  };

  const handleCheckAllIssues = (e: ChangeEvent<HTMLInputElement>) => {
    displayedIssues.forEach((issue) => {
      const instanceIds = issue.getInstances();
      if (e.target.checked) {
        exportState.selectAllInstances(instanceIds, issue.getNumber());
      } else {
        exportState.unselectAllInstances(instanceIds, issue.getNumber());
      }
    });
  };

  return (
    <Accordion className="issue-group" onChange={(_, open) => (!isJiraEnhancementsEnabled ? setOpen(open) : null)} expanded={open}>
      {isJiraEnhancementsEnabled ? (
        <AccordionSummary
          id={`issue-group-${availableSeverities.join('_')}-header`}
          aria-controls={`issue-group-${availableSeverities.join('_')}-content`}
          expandIcon={
            <IconButton
              onClick={() => setOpen((o) => !o)}
              aria-label={open ? t('a11y.report.issues.hideIssueList', { category: title }) : t('a11y.report.issues.showIssueList', { category: title })}
            >
              <ExpandMore />
            </IconButton>
          }
          title={t('a11y.project.dashboard.issueAccordion', { severity: severityLabels.join(', ') })}
          sx={{ minHeight: '4rem', cursor: 'unset !important', '&.MuiAccordionSummary-root.Mui-focusVisible': { backgroundColor: 'unset !important' } }}
        >
          <Stack direction="row" gap={1} alignItems="center" sx={{ width: 1, pl: 1.125 }}>
            {canMultiSelect && (
              <Checkbox
                sx={{ m: -1.125 }}
                checked={selectedStatus === CheckedStatus.SELECTED}
                indeterminate={selectedStatus === CheckedStatus.PARTIALLY_SELECTED}
                onChange={(e) => handleCheckAllIssues(e)}
                data-role="issue-select-all-checkbox"
                disabled={displayedIssues.length === 0}
                inputProps={{ 'aria-label': t('a11y.report.issues.selectAllIssues'), role: 'checkbox' }}
              />
            )}
            <Typography>{title}</Typography>
            <Tooltip title={tooltip} placement="top" arrow>
              <InfoOutlined sx={{ color: 'grey.600' }} fontSize="small" />
            </Tooltip>
            <Typography variant="body2" color="text.secondary" textTransform="uppercase">
              ({t('issues', { count: issues.length })})
            </Typography>
          </Stack>
        </AccordionSummary>
      ) : (
        <AccordionSummary
          id={`issue-group-${availableSeverities.join('_')}-header`}
          aria-controls={`issue-group-${availableSeverities.join('_')}-content`}
          expandIcon={<ExpandMore />}
          title={t('a11y.project.dashboard.issueAccordion', { severity: severityLabels.join(', ') })}
          sx={{ minHeight: '4rem' }}
        >
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: 1 }}>
            <Typography>{title}</Typography>
            <Tooltip title={tooltip} placement="top" arrow>
              <InfoOutlined sx={{ color: 'grey.600' }} fontSize="small" />
            </Tooltip>
            <Typography variant="body2" color="text.secondary" textTransform="uppercase">
              ({t('issues', { count: issues.length })})
            </Typography>
          </Stack>
        </AccordionSummary>
      )}
      <AccordionDetails sx={{ p: 0 }}>
        {severityFilters.length > 1 && <SeverityFilter severities={severityFilters} onChange={onFilterChange} />}
        {displayedIssues.map((issue: Issue) => {
          const isSelected = issue.getNumber() === selectedIssueNumber;
          const availableIds = issue.getInstances();
          const selectedStatus = exportState.getIssueSelectionState(availableIds, issue.getNumber());
          return (
            <Box key={issue.getNumber()} ref={isSelected ? selectedIssueRef : undefined}>
              {isJiraEnhancementsEnabled ? (
                <MultiSelectItemCard
                  className={`issue-${issue.getNumber()}-${issue.getSeverity()}`}
                  backgroundColor={isSelected ? SEVERITY_COLORS[issue.getSeverity()].fadedBackground : undefined}
                  borderLeftColor={SEVERITY_COLORS[issue.getSeverity()].main}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center" width={1}>
                    {canMultiSelect && (
                      <Checkbox
                        checked={selectedStatus === CheckedStatus.SELECTED}
                        indeterminate={selectedStatus === CheckedStatus.PARTIALLY_SELECTED}
                        onChange={(e) => handleIssueClick(e, issue)}
                        data-role={`issue-${issue.getNumber()}-checkbox`}
                        inputProps={{ 'aria-label': t('a11y.report.issues.selectIssue', { issueNumber: issue.getNumber() }) }}
                      />
                    )}
                    <Stack direction={'column'} flexGrow={1}>
                      <FilterStatusBadge filterStatus={issue.getFilterStatus()} noBorder sx={{ justifyContent: 'flex-start' }} />
                      <Typography sx={isSelected ? { textDecoration: 'underline' } : {}}>
                        {issue.getNumber()}. {issue.getTitle()}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', pt: 0.75 }}>
                        {issue.getShortExplanation()}
                      </Typography>
                    </Stack>
                    <IconButton
                      sx={{ ml: 'auto' }}
                      aria-label={t('a11y.report.issues.issueListItem', {
                        issueNumber: issue.getNumber(),
                        issueName: issue.getTitle(),
                        severity: getLabel(issue.getSeverity()),
                        filterStatus: getFilterStatusLabel(issue.getFilterStatus(), { omitFullyKept: true }),
                      })}
                      href={UrlHelper.generatePath(
                        PATHS.REPORT_ISSUE,
                        { reportId: issue.getReportId().toString(), issueId: issue.getNumber().toString() },
                        { keepQueryParams: true }
                      )}
                    >
                      <KeyboardArrowRight />
                    </IconButton>
                  </Stack>
                </MultiSelectItemCard>
              ) : (
                <ItemCard
                  tabIndex={0}
                  backgroundColor={isSelected ? SEVERITY_COLORS[issue.getSeverity()].fadedBackground : undefined}
                  borderLeftColor={SEVERITY_COLORS[issue.getSeverity()].main}
                  ariaLabel={t('a11y.report.issues.issueListItem', {
                    issueNumber: issue.getNumber(),
                    issueName: issue.getTitle(),
                    severity: getLabel(issue.getSeverity()),
                    filterStatus: getFilterStatusLabel(issue.getFilterStatus(), { omitFullyKept: true }),
                  })}
                  href={UrlHelper.generatePath(
                    PATHS.REPORT_ISSUE,
                    { reportId: issue.getReportId().toString(), issueId: issue.getNumber().toString() },
                    { keepQueryParams: true }
                  )}
                >
                  <FilterStatusBadge filterStatus={issue.getFilterStatus()} noBorder />
                  <Typography sx={isSelected ? { textDecoration: 'underline' } : {}}>
                    {issue.getNumber()}. {issue.getTitle()}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', pt: 0.75 }}>
                    {issue.getShortExplanation()}
                  </Typography>
                </ItemCard>
              )}
            </Box>
          );
        })}
        {displayedIssues.length === 0 && (
          <Box sx={{ py: 6, borderTop: '1px solid', borderColor: 'grey.100' }}>
            <CustomNoRowsOverlay label={t('issueListEmpty')} />
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
});
